import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
// @ts-ignore: No TypeScript support
import makeComponentTrashable from 'trashable-react';
import { useTranslation, useStatusMessage, useLocalNotificationBanner } from 'Shared/util/custom-hooks';
import { updateUserSsn } from 'Shared/util/web-api';
import { EIdAuthenticationPurpose } from 'Shared/util/types';
import { useEIdAuthentication } from 'Shared/util/eid-authentication/eid-authentication-hook';
import useAppContext from 'Contexts/app-context';
import useUserContext from 'Contexts/user-context';
import { joinClassNames as c } from 'Shared/util/styles';
import StatusMessage from 'Shared/components/status-message';
import Spinner from 'Shared/components/spinner';
import TextLink from 'Shared/components/text-link';
import Button from 'Shared/components/button';
import ReturnTitle from 'Shared/components/return-title';
import styles from './bankid-tab.css';

type BankIdTabProps = {
  registerPromise: (p: Promise<any>) => Promise<any>;
  display: boolean;
  onHide: (action?: string) => void;
  onSucceeded: () => void;
  openBanking: boolean;
};

const BankIdTab = ({
  registerPromise,
  display,
  onHide,
  onSucceeded,
  openBanking,
}: BankIdTabProps) => {
  const { updateUserSessionToken } = useUserContext();
  const { appData: { eIdProvider, privacyPolicyUrl } } = useAppContext();
  const [statusMessage, dispatchStatusMessage] = useStatusMessage();
  const [isSaving, setIsSaving] = useState(false);
  const history = useHistory();
  const t = useTranslation(x => x.bankId);
  const tg = useTranslation(x => x.global);
  const tdb = useTranslation(x => x.dbTranslations);
  const te = useTranslation(x => x.eId);
  const { NotificationBannerType, addLocalNotificationBanner } = useLocalNotificationBanner(false);

  const handleEIdAuthenticationSucceeded = (token: string) => {
    setIsSaving(true);
    registerPromise(updateUserSsn(token))
      .then((userToken) => {
        if (userToken) {
          updateUserSessionToken && updateUserSessionToken(userToken);
          onSucceeded();
          
          if (!openBanking) addLocalNotificationBanner(t.bankidHasNowBeenActivatedForYourAccountAndSocialSecurityNumber, NotificationBannerType.Success);
        }
      })
      .catch(() => dispatchStatusMessage({ type: 'FAILED', message: t.sorryWeCouldNotSaveYourChangesTryAgain }))
      .finally(() => setIsSaving(false));
  };
  const handleEIdAuthenticationFailed = () => dispatchStatusMessage({ type: 'FAILED', message: tdb.eidVerificationAborted });
  const handleEIdAuthenticationError = () => dispatchStatusMessage({ type: 'FAILED', message: t.sorryWeCouldNotSaveYourChangesTryAgain });
  const handleEIdAuthenticationStarted = () => dispatchStatusMessage({ type: 'NONE' });
  const {
    renderEIdAuthentication,
    resetEIdAuthentication,
    isAuthenticatingEId
  } = useEIdAuthentication(
    registerPromise,
    EIdAuthenticationPurpose.EIdActivation,
    handleEIdAuthenticationSucceeded,
    handleEIdAuthenticationFailed,
    handleEIdAuthenticationError,
    handleEIdAuthenticationStarted
  );

  useEffect(() => {
    if (!display) {
      resetEIdAuthentication();
    }
  }, [display]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (display) {
      params.append('sidebar', 'activate-eid');
    } else {
      params.delete("sidebar");
    }
    history.replace({ search: params.toString() });
  }, [display, history]);

  const handleHide = () => {
    onHide();
    resetEIdAuthentication();
  };

  const eIdFormatArgs = te[eIdProvider];

  const showPrivacyPolicy = privacyPolicyUrl && !isAuthenticatingEId && !isSaving;

  return (
    <div className={c(styles.container, (display ? styles.visible : styles.hidden))}>
      <StatusMessage
        status={statusMessage.type}
        text={statusMessage.message}
        clearStatus={() => dispatchStatusMessage({ type: 'NONE' })}
        className={styles.statusMessageContainer}
      />
      {!openBanking ? (
          <ReturnTitle
              title={te.format(te.activate, eIdFormatArgs)}
              onClick={onHide}
              display
          />
      ) : (
          <h3 className={styles.title}>{te.format(te.activate, eIdFormatArgs)}</h3>
      )}
      <div className={styles.content}>
        {(!isAuthenticatingEId && !isSaving) && (
          <p className={styles.description}>
            {!openBanking ? te.format(te.activation_message, eIdFormatArgs) : te.format(te.activation_message_open_banking, eIdFormatArgs)}
          </p>
        )}
        {isSaving
          ? <Spinner />
          : renderEIdAuthentication()
        }
        {showPrivacyPolicy && (
          <TextLink
            text={te.privacyPolicy}
            link={privacyPolicyUrl}
            className={styles.privacyPolicyLink}
          />
        )}
        <div className={c(styles.buttonsContainer, (openBanking ? styles.hidden : ''))}>
          <Button
            buttonType="button-secondary"
            text={tg.cancel}
            onClick={handleHide}
          />
        </div>
      </div>
    </div>
  );
};


export default makeComponentTrashable(BankIdTab);
